import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useAppDispatch } from "@/store";
import {
  useLazyGetDatasetQuestionsQuery,
  useLazyGetDatasetsPaginatedQuery,
  useLazyGetDatasetQuestionsListQuery,
} from "@/store/api/dataset-api";
import { showErrorSnackbar } from "@/store/slices/snackbar-slice";
import { t } from "@/utils/i18n";
import { useDebounce } from "@/hooks/use-debounce";
import type { Dataset, DatasetQuestionsSearchResponse } from "@/models/Dataset";
import type {
  ScorecardBrand,
  ScorecardBrandCandidate,
  ScorecardSourceQuestion,
} from "@/models/Scorecard";
import type { Client } from "@/models/Dashboards";
import { groupDatasets } from "@/utils/group-datasets";
import {
  getBrandCandidates,
  getSourceQuestionKind,
  isEligibleSourceBrandQuestionSummary,
} from "@/utils/scorecard-utils";

interface UseScorecardModalProps {
  hasActiveFilters?: boolean;
  existingDatasetIds?: string[];
  primaryClient?: Client;
  additionalClients?: Client[];
  dashboardId?: string;
}

// Dataset + question list pagination state — mirrors use-chart-modal.ts
// exactly, so the Scorecard creation flow behaves identically to every other
// chart type's dataset/question pickers (In Use grouping, filter restriction,
// infinite scroll, auto-select, recent questions).
interface ModalListState {
  datasetSearchTerm: string;
  datasetPage: number;
  allDatasets: Dataset[];
  datasetsHasMore: boolean;
  datasetsHasError: boolean;
  isAutoSelected: boolean;
  currentPage: number;
  allQuestions: DatasetQuestionsSearchResponse[];
  hasMore: boolean;
  hasError: boolean;
}

type ModalListAction =
  | { type: "SET_DATASET_SEARCH_TERM"; payload: string }
  | { type: "SET_DATASET_PAGE"; payload: number }
  | { type: "SET_ALL_DATASETS"; payload: Dataset[] }
  | { type: "APPEND_DATASETS"; payload: Dataset[] }
  | { type: "SET_DATASETS_HAS_MORE"; payload: boolean }
  | { type: "SET_DATASETS_HAS_ERROR"; payload: boolean }
  | { type: "RESET_DATASET_PAGINATION" }
  | { type: "SET_IS_AUTO_SELECTED"; payload: boolean }
  | { type: "SET_CURRENT_PAGE"; payload: number }
  | { type: "SET_ALL_QUESTIONS"; payload: DatasetQuestionsSearchResponse[] }
  | { type: "APPEND_QUESTIONS"; payload: DatasetQuestionsSearchResponse[] }
  | { type: "SET_HAS_MORE"; payload: boolean }
  | { type: "SET_HAS_ERROR"; payload: boolean }
  | { type: "RESET_PAGINATION" };

const initialListState: ModalListState = {
  datasetSearchTerm: "",
  datasetPage: 1,
  allDatasets: [],
  datasetsHasMore: true,
  datasetsHasError: false,
  isAutoSelected: false,
  currentPage: 1,
  allQuestions: [],
  hasMore: true,
  hasError: false,
};

function listReducer(
  state: ModalListState,
  action: ModalListAction,
): ModalListState {
  switch (action.type) {
    case "SET_DATASET_SEARCH_TERM":
      return { ...state, datasetSearchTerm: action.payload };
    case "SET_DATASET_PAGE":
      return { ...state, datasetPage: action.payload };
    case "SET_ALL_DATASETS":
      return { ...state, allDatasets: action.payload };
    case "APPEND_DATASETS": {
      const existingIds = new Set(state.allDatasets.map((d) => d.id));
      const newDatasets = action.payload.filter((d) => !existingIds.has(d.id));
      return { ...state, allDatasets: [...state.allDatasets, ...newDatasets] };
    }
    case "SET_DATASETS_HAS_MORE":
      return { ...state, datasetsHasMore: action.payload };
    case "SET_DATASETS_HAS_ERROR":
      return { ...state, datasetsHasError: action.payload };
    case "RESET_DATASET_PAGINATION":
      return {
        ...state,
        datasetPage: 1,
        allDatasets: [],
        datasetsHasMore: true,
        datasetsHasError: false,
      };
    case "SET_IS_AUTO_SELECTED":
      return { ...state, isAutoSelected: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_ALL_QUESTIONS":
      return { ...state, allQuestions: action.payload };
    case "APPEND_QUESTIONS": {
      const existingIds = new Set(
        state.allQuestions.map((q) => q.datasetElementId),
      );
      const newQuestions = action.payload.filter(
        (q) => !existingIds.has(q.datasetElementId),
      );
      return { ...state, allQuestions: [...state.allQuestions, ...newQuestions] };
    }
    case "SET_HAS_MORE":
      return { ...state, hasMore: action.payload };
    case "SET_HAS_ERROR":
      return { ...state, hasError: action.payload };
    case "RESET_PAGINATION":
      return { ...state, currentPage: 1, allQuestions: [], hasMore: true, hasError: false };
    default:
      return state;
  }
}

export const useScorecardModal = (props?: UseScorecardModalProps) => {
  const {
    hasActiveFilters = false,
    existingDatasetIds = [],
    primaryClient,
    additionalClients = [],
  } = props || {};
  const dispatch = useAppDispatch();

  const [datasetId, setDatasetId] = useState("");
  const [datasetVersionId, setDatasetVersionId] = useState<number | null>(null);
  const [questionSearchTerm, setQuestionSearchTerm] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState("");
  const [brandCandidates, setBrandCandidates] = useState<ScorecardBrandCandidate[]>([]);
  const [sourceQuestion, setSourceQuestion] = useState<ScorecardSourceQuestion | null>(null);
  const [primaryBrandSourceId, setPrimaryBrandSourceId] = useState<string | null>(null);
  const [competitorBrandSourceIds, setCompetitorBrandSourceIds] = useState<string[]>([]);

  const [state, dispatchList] = useReducer(listReducer, initialListState);

  const observerTarget = useRef<HTMLDivElement>(null);
  const observerTargetDatasets = useRef<HTMLDivElement>(null);

  const debouncedDatasetSearch = useDebounce(state.datasetSearchTerm, 400);
  const debouncedQuestionSearch = useDebounce(questionSearchTerm, 500);

  const [triggerDatasets, { error: datasetsError, isFetching: isFetchingDatasets }] =
    useLazyGetDatasetsPaginatedQuery();
  const [triggerQuestions, { data: questionsData, error: questionsError, isFetching }] =
    useLazyGetDatasetQuestionsQuery();
  const [fetchQuestionDetails, { isFetching: isFetchingQuestionDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  // ── Dataset list: load first page when search term changes ──────────────
  useEffect(() => {
    dispatchList({ type: "RESET_DATASET_PAGINATION" });
    triggerDatasets({
      state: "active",
      name: debouncedDatasetSearch || undefined,
      page: 1,
      page_size: 30,
    })
      .unwrap()
      .then((res) => {
        dispatchList({ type: "SET_ALL_DATASETS", payload: res.datasets });
        dispatchList({
          type: "SET_DATASETS_HAS_MORE",
          payload: res.pagination.hasNextPage,
        });
      })
      .catch(() => dispatchList({ type: "SET_DATASETS_HAS_ERROR", payload: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDatasetSearch, hasActiveFilters]);

  // ── Dataset list: infinite scroll ─────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          state.datasetsHasMore &&
          !isFetchingDatasets &&
          !state.datasetsHasError
        ) {
          const nextPage = state.datasetPage + 1;
          dispatchList({ type: "SET_DATASET_PAGE", payload: nextPage });
          triggerDatasets({
            state: "active",
            name: debouncedDatasetSearch || undefined,
            page: nextPage,
            page_size: 10,
          })
            .unwrap()
            .then((res) => {
              dispatchList({ type: "APPEND_DATASETS", payload: res.datasets });
              dispatchList({
                type: "SET_DATASETS_HAS_MORE",
                payload: res.pagination.hasNextPage,
              });
            })
            .catch(() => {
              dispatchList({ type: "SET_DATASETS_HAS_ERROR", payload: true });
              dispatchList({ type: "SET_DATASETS_HAS_MORE", payload: false });
            });
        }
      },
      { threshold: 0.1 },
    );
    const el = observerTargetDatasets.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [
    state.datasetsHasMore,
    isFetchingDatasets,
    state.datasetsHasError,
    state.datasetPage,
    debouncedDatasetSearch,
    triggerDatasets,
  ]);

  // ── Auto-select when exactly one dataset is in use ───────────────────────
  useEffect(() => {
    if (
      existingDatasetIds.length === 1 &&
      state.allDatasets.length > 0 &&
      !datasetId
    ) {
      const id = existingDatasetIds[0];
      const dataset = state.allDatasets.find((d) => d.id === id);
      if (dataset) {
        setDatasetId(id);
        setDatasetVersionId(dataset.datasetVersionId ?? null);
        dispatchList({ type: "SET_IS_AUTO_SELECTED", payload: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.allDatasets, existingDatasetIds]);

  // ── Error handling ────────────────────────────────────────────────────────
  useEffect(() => {
    if (datasetsError) dispatch(showErrorSnackbar({ message: t("common.error") }));
  }, [datasetsError, dispatch]);

  useEffect(() => {
    if (questionsError) {
      dispatch(showErrorSnackbar({ message: t("common.error") }));
      dispatchList({ type: "SET_HAS_ERROR", payload: true });
      dispatchList({ type: "SET_HAS_MORE", payload: false });
    }
  }, [questionsError, dispatch]);

  // ── Question list: load first page when dataset or search term changes ──
  useEffect(() => {
    if (!datasetId) return;
    dispatchList({ type: "RESET_PAGINATION" });
    triggerQuestions({
      id: datasetId,
      page: 1,
      page_size: 30,
      question_type: ["SINGLE", "MULTI"],
      multidimensional: true,
      search_query: debouncedQuestionSearch || undefined,
    })
      .unwrap()
      .catch(() => dispatchList({ type: "SET_HAS_ERROR", payload: true }));
  }, [datasetId, debouncedQuestionSearch, triggerQuestions]);

  // ── Question list: accumulate as pages load ──────────────────────────────
  useEffect(() => {
    if (questionsData?.questions) {
      if (state.currentPage === 1) {
        dispatchList({ type: "SET_ALL_QUESTIONS", payload: questionsData.questions });
      } else {
        dispatchList({ type: "APPEND_QUESTIONS", payload: questionsData.questions });
      }
      dispatchList({
        type: "SET_HAS_MORE",
        payload: questionsData.pagination.hasNextPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsData]);

  // ── Question list: infinite scroll ───────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          state.hasMore &&
          !isFetching &&
          !state.hasError &&
          datasetId
        ) {
          const nextPage = state.currentPage + 1;
          dispatchList({ type: "SET_CURRENT_PAGE", payload: nextPage });
          triggerQuestions({
            id: datasetId,
            page: nextPage,
            page_size: 10,
            question_type: ["SINGLE", "MULTI"],
            multidimensional: true,
            search_query: debouncedQuestionSearch || undefined,
          })
            .unwrap()
            .catch(() => {
              dispatchList({ type: "SET_HAS_ERROR", payload: true });
              dispatchList({ type: "SET_HAS_MORE", payload: false });
            });
        }
      },
      { threshold: 0.1 },
    );
    const el = observerTarget.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [
    state.hasMore,
    isFetching,
    state.hasError,
    datasetId,
    state.currentPage,
    debouncedQuestionSearch,
    triggerQuestions,
  ]);

  // ── Computed values ───────────────────────────────────────────────────────
  const datasetGroups = useMemo(() => {
    const baseDatasets =
      hasActiveFilters && existingDatasetIds.length > 0
        ? state.allDatasets.filter((d) => existingDatasetIds.includes(d.id))
        : state.allDatasets;
    return groupDatasets(baseDatasets, existingDatasetIds, primaryClient, additionalClients);
  }, [state.allDatasets, hasActiveFilters, existingDatasetIds, primaryClient, additionalClients]);

  const filteredQuestions = useMemo(
    () => state.allQuestions.filter((q) => isEligibleSourceBrandQuestionSummary(q)),
    [state.allQuestions],
  );

  const handleDatasetChange = (id: string, versionId?: number | null) => {
    setDatasetId(id);
    setDatasetVersionId(versionId ?? null);
    setSelectedQuestionId("");
    setSourceQuestion(null);
    setBrandCandidates([]);
    setPrimaryBrandSourceId(null);
    setCompetitorBrandSourceIds([]);
  };

  const handleDatasetSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    dispatchList({ type: "SET_DATASET_SEARCH_TERM", payload: event.target.value });

  const handleQuestionSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setQuestionSearchTerm(event.target.value);

  const handleSourceQuestionSelect = async (
    question: DatasetQuestionsSearchResponse,
  ) => {
    setSelectedQuestionId(question.datasetElementId);
    setPrimaryBrandSourceId(null);
    setCompetitorBrandSourceIds([]);

    if (!datasetId) return;
    const result = await fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: question.label,
      dataset_element_id: question.datasetElementId,
      expanded: true,
    }).unwrap();

    const detail = result.questions.find(
      (q) => q.datasetElementId === question.datasetElementId,
    );
    if (!detail) return;

    const kind = getSourceQuestionKind(detail);
    if (!kind) return;

    const candidates = getBrandCandidates(detail);
    setBrandCandidates(candidates);
    setSourceQuestion({
      questionId: detail.datasetElementId,
      questionLabel: detail.label,
      questionTitle: detail.title,
      questionType: (detail.type?.toUpperCase() === "MULTI"
        ? "MULTIPLE"
        : detail.type?.toUpperCase()) as "SINGLE" | "MULTIPLE",
      kind,
    });
  };

  const handlePrimaryBrandChange = (sourceId: string) => {
    setPrimaryBrandSourceId(sourceId);
    setCompetitorBrandSourceIds((prev) => prev.filter((id) => id !== sourceId));
  };

  const handleCompetitorsChange = (sourceIds: string[]) => {
    setCompetitorBrandSourceIds(sourceIds.filter((id) => id !== primaryBrandSourceId));
  };

  const brands: ScorecardBrand[] = useMemo(() => {
    const result: ScorecardBrand[] = [];
    const primary = brandCandidates.find((c) => c.sourceId === primaryBrandSourceId);
    if (primary) {
      result.push({
        id: crypto.randomUUID(),
        role: "primary",
        sourceId: primary.sourceId,
        label: primary.label,
        sourceOrder: primary.sourceOrder,
      });
    }
    const competitors = brandCandidates
      .filter((c) => competitorBrandSourceIds.includes(c.sourceId))
      .sort((a, b) => a.sourceOrder - b.sourceOrder);
    competitors.forEach((c) =>
      result.push({
        id: crypto.randomUUID(),
        role: "competitor",
        sourceId: c.sourceId,
        label: c.label,
        sourceOrder: c.sourceOrder,
      }),
    );
    return result;
  }, [brandCandidates, primaryBrandSourceId, competitorBrandSourceIds]);

  const isCreateEnabled = Boolean(
    datasetId && sourceQuestion && primaryBrandSourceId && competitorBrandSourceIds.length >= 1,
  );

  return {
    datasetId,
    datasetVersionId,
    datasetGroups,
    datasetSearchTerm: state.datasetSearchTerm,
    isFetchingDatasets,
    datasetsHasMore: state.datasetsHasMore,
    observerTargetDatasets,
    isAutoSelected: state.isAutoSelected,
    isDatasetRestricted: hasActiveFilters && existingDatasetIds.length > 0,
    handleDatasetChange,
    handleDatasetSearchChange,

    questionSearchTerm,
    selectedQuestionId,
    filteredQuestions,
    isFetching,
    hasMore: state.hasMore,
    isFetchingQuestionDetails,
    observerTarget,
    handleQuestionSearchChange,
    handleSourceQuestionSelect,

    sourceQuestion,
    brandCandidates,
    primaryBrandSourceId,
    competitorBrandSourceIds,
    handlePrimaryBrandChange,
    handleCompetitorsChange,
    brands,
    isCreateEnabled,
  };
};
